import { TemplateElement, html, defineElement, when } from '@webtides/element-js';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock/lib/bodyScrollLock.es6';
import * as focusTrap from 'focus-trap';
import style from './modal.css';
import '../button/button.js';
import '../icon/icon.js';

/**
 * Modal component to make a dialog declarative and accessible
 * @property {boolean} previewMode
 * @property {boolean} deepLinking
 * @property {boolean} autoOpen
 * @property {boolean} isOpen
 * @property {'top' | 'center' | 'bottom' | 'fullscreen'} placement
 * @property {string} label
 * @property {string} closeLabel
 * @property {boolean} showCloseButton
 * @property {boolean} contentOnly
 */
export default class Modal extends TemplateElement {
    constructor() {
        super({ styles: [style], shadowRender: true });
    }

    properties() {
        return {
            previewMode: false,
            deepLinking: true,
            autoOpen: false,
            isOpen: false,
            placement: 'center',
            label: '',
            closeLabel: '',
            showCloseButton: true,
            contentOnly: false,
        };
    }

    connected() {
        // TODO: handle case with no id
        const triggers = document.querySelectorAll(`[show-modal="${this.id}"]`);
        for (const trigger of triggers) {
            trigger.addEventListener('click', (event) => {
                this.show();
            });
        }

        this.focusTrap = focusTrap.createFocusTrap([this, this.$refs.dialog], {
            fallbackFocus: this,
        });

        if ((this.deepLinking && window.location.hash.includes(this.id)) || this.autoOpen) {
            setTimeout(() => {
                this.show();
            }, 0);
        }

        setTimeout(() => {
            this.adoptStyleSheets();
        }, 100);
    }

    watch() {
        return {
            isOpen: (open) => {
                if (open) {
                    this.$refs.dialog.showModal();
                    this.dispatch('modal-open');
                    if (this.deepLinking) {
                        window.location.hash = this.id;
                    }
                    this._internals.states.add('open');
                    //TODO disableBodyScroll and focusTrap dont work
                    disableBodyScroll(this.$refs.dialog, {
                        reserveScrollBarGap: true,
                    });
                    this.focusTrap.activate();
                } else {
                    this.$refs.dialog.close();
                    this.dispatch('modal-close');
                    const [url, hash] = window.location.href.split('#');
                    window.history.pushState(null, null, url);
                    this._internals.states.delete('open');
                    enableBodyScroll(this.$refs.dialog);
                    this.focusTrap.deactivate();
                }
            },
        };
    }

    events() {
        return {
            window: {
                hashchange: () => {
                    if (window.location.hash.substring(1) === this.id && !this.isOpen) {
                        this.show();
                    } else if (!window.location.hash.substring(1) === this.id && this.isOpen) {
                        this.close();
                    }
                },
            },
            dialog: {
                close: () => {
                    this.isOpen = false;
                },
                cancel: () => {
                    this.isOpen = false;
                },
                click: (event) => {
                    if (event.target.nodeName === 'DIALOG') {
                        this.$refs.dialog.close('dismiss');
                    }
                },
            },
            '[close-modal]': {
                click: () => {
                    this.close();
                },
            },
        };
    }

    show() {
        this.isOpen = true;
    }

    close() {
        this.isOpen = false;
    }

    template() {
        return html`
            <dialog
                ref="dialog"
                placement="${this.placement}"
                preview-mode="${this.previewMode}"
                content-only="${this.contentOnly}"
            >
                <div ref="content" class="content">
                    ${when(
                        this.showCloseButton,
                        html`
                            <button class="Modal--closeBtn" close-modal aria-label="${this.closeLabel || 'Close'}">
                                <svg
                                    class="Modal--closeIcon"
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                    aria-hidden="true"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                >
                                    <path d="M6 6l12 12M18 6L6 18" />
                                </svg>
                            </button>
                        `,
                    )}
                    ${when(
                        this.label,
                        html`
                            <header>
                                <slot name="header" ref="headerSlot"><h2>${this.label}</h2></slot>
                            </header>
                        `,
                    )}
                    <main>
                        <slot ref="mainSlot"></slot>
                    </main>
                    <footer>
                        <slot name="footer" ref="footerSlot"></slot>
                    </footer>
                </div>
            </dialog>
        `;
    }
}

defineElement('mb-modal', Modal);
